export type ResourceTypeFieldAssignmentBase = {
  id: string;
  required: boolean;
  locked: boolean;
  /** Mirrors `metastructure.fields[].active`. */
  active: boolean;
  order: number;
};

export type ResourceTypeFieldAssignment =
  | (ResourceTypeFieldAssignmentBase & {
      source: 'core';
      coreFieldKey: string;
    })
  | (ResourceTypeFieldAssignmentBase & {
      source: 'custom';
      customFieldDefinitionId: number;
    })
  /** A field group; only on the global metastructure, never in a workspace overlay. */
  | (ResourceTypeFieldAssignmentBase & {
      source: 'group';
      customFieldGroupDefinitionId: number;
    });

export type ResourceTypeWorkspaceBinding = {
  resourceTypeId: number;
  workspaceId: number;
  enabled: boolean;
  workspaceFields: ResourceTypeFieldAssignment[];
};

export type SetResourceTypeEnabledInput = {
  organizationId: string;
  resourceTypeId: number;
  workspaceId: number;
  enabled: boolean;
};

export type SetResourceTypeWorkspaceFieldsInput = {
  organizationId: string;
  resourceTypeId: number;
  workspaceId: number;
  workspaceFields: ResourceTypeFieldAssignment[];
};

/**
 * Resource-type ↔ workspace bindings.
 * Connected: HTTP. Storybook / tests: mock + in-memory ReadWriteApi.
 */
export interface ResourceTypeBindingsApi {
  list(organizationId: string): Promise<ResourceTypeWorkspaceBinding[]>;
  setEnabled(
    input: SetResourceTypeEnabledInput
  ): Promise<ResourceTypeWorkspaceBinding>;
  setWorkspaceFields(
    input: SetResourceTypeWorkspaceFieldsInput
  ): Promise<ResourceTypeWorkspaceBinding>;
}

export const resourceTypeBindingsUtils = {
  /**
   * Missing binding → not enabled in the workspace (opt-in via setEnabled).
   */
  resolveBinding(
    bindings: ResourceTypeWorkspaceBinding[],
    resourceTypeId: number,
    workspaceId: number
  ): ResourceTypeWorkspaceBinding {
    const existing = bindings.find(
      binding =>
        binding.resourceTypeId === resourceTypeId &&
        binding.workspaceId === workspaceId
    );
    return (
      existing ?? {
        resourceTypeId,
        workspaceId,
        enabled: false,
        workspaceFields: []
      }
    );
  },

  isEnabledInWorkspace(
    bindings: ResourceTypeWorkspaceBinding[],
    resourceTypeId: number,
    workspaceId: number
  ): boolean {
    return resourceTypeBindingsUtils.resolveBinding(
      bindings,
      resourceTypeId,
      workspaceId
    ).enabled;
  }
};
